import { useState, useRef, useCallback } from 'react';
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';
import usePermission from './usePermission';

/**
 * The gate helper. Nothing calls it yet - it ships now so that adding a gate later is a two-line
 * change in the screen that needs it, not a new mechanism invented under time pressure.
 *
 * If the staff member already holds the permission it resolves straight away with their own id and
 * no popup. Otherwise it prompts for an authorizing PIN and resolves only if that PIN belongs to
 * someone who does hold it - without changing who is active on the till, because a manager
 * authorizing one void should not silently take over the server's session.
 *
 * Resolves `{ authorized, employeeId }`. `employeeId` is whoever actually authorized the action,
 * which is the id to attribute it to (a `del_emp_id` on a delete, say) - not necessarily whoever
 * is signed in.
 *
 * IMPORTANT for whoever adds the first gate: this fails closed. On a restaurant where nobody holds
 * the permission, the action becomes impossible - no PIN in the building can satisfy the prompt.
 * The backfill migration reports which restaurants are in that state; each needs at least one
 * staff member on Manager or Admin before a gate is switched on for them.
 */
export default function useRequirePermission() {
  const hasPermission = usePermission();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const pendingRef = useRef(null);

  const requirePermission = useCallback((permissionKey) => {
    if (hasPermission(permissionKey)) {
      const employeeId = localStorage.getItem('active_staff_id');
      return Promise.resolve({ authorized: true, employeeId: employeeId ? Number(employeeId) : null });
    }

    return new Promise((resolve) => {
      pendingRef.current = { permissionKey, resolve };
      setError('');
      setIsOpen(true);
    });
  }, [hasPermission]);

  const handleConfirm = async (pin) => {
    const pending = pendingRef.current;
    if (!pending) {
      return;
    }

    setBusy(true);
    setError('');
    try {
      const response = await callAPI(apiUrls.STAFF_AUTH_VALIDATE_PIN, {}, 'POST', { pin });
      const permissions = response.data?.data?.permissions || [];
      const employeeId = response.data?.data?.employee_id ?? null;

      if (permissions.includes(pending.permissionKey)) {
        pendingRef.current = null;
        setIsOpen(false);
        pending.resolve({ authorized: true, employeeId });
      } else {
        // Distinguished from an unrecognised PIN on purpose: "your PIN works but you may not do
        // this" and "that isn't a PIN" send the user to different next steps.
        setError('That PIN is not authorized for this action.');
      }
    } catch (e) {
      setError('PIN not recognised.');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setIsOpen(false);
    if (pending) {
      pending.resolve({ authorized: false, employeeId: null });
    }
  };

  return {
    requirePermission,
    // Spread straight onto <StaffPinModal {...permissionModalProps} /> at the bottom of the screen
    // that uses the gate.
    permissionModalProps: {
      isOpen,
      onCancel: handleCancel,
      onConfirm: handleConfirm,
      title: 'Manager Authorization Required',
      subtitle: 'Enter an authorizing PIN to continue',
      error,
      busy,
    },
  };
}
